import { Injectable, computed, signal } from '@angular/core';

export type RenderPhase = 'idle' | 'preparing' | 'rendering' | 'uploading' | 'done' | 'error';

export interface RenderStatus {
  phase: RenderPhase;
  /** 0..1 for determinate, -1 for indeterminate */
  progress: number;
  title: string;
  sub?: string | null;
  device?: string | null;
  encoder?: string | null;
  cancelable?: boolean;
  retryable?: boolean;
  error?: string | null;
}

const IDLE_STATUS: RenderStatus = {
  phase: 'idle',
  progress: -1,
  title: '',
  sub: null,
  device: null,
  encoder: null,
  cancelable: false,
  retryable: false,
  error: null
};

@Injectable({ providedIn: 'root' })
export class RenderStatusService {
  private readonly _status = signal<RenderStatus>({ ...IDLE_STATUS });
  private _cancelFn: (() => void) | null = null;
  private _retryFn: (() => void) | null = null;
  private _resetTimer: ReturnType<typeof setTimeout> | null = null;

  /** The current render/prepare status. */
  readonly status = this._status.asReadonly();

  /** True whenever phase !== 'idle'. */
  readonly visible = computed(() => this._status().phase !== 'idle');

  /**
   * Start a new render/prepare operation. Clears any pending auto-reset timer.
   * @param partial Must include at least `phase`.
   */
  begin(partial: Partial<RenderStatus> & { phase: RenderPhase }): void {
    this.clearResetTimer();
    this._retryFn = null;
    this._status.set({
      ...IDLE_STATUS,
      ...partial,
      cancelable: this._cancelFn !== null || (partial.cancelable ?? false),
      retryable: false
    });
  }

  /**
   * Patch one or more fields of the current status.
   * Does NOT change cancelable unless explicitly provided — a registered cancel handler
   * keeps its effect.
   */
  update(partial: Partial<RenderStatus>): void {
    this._status.update(current => ({
      ...current,
      ...partial,
      cancelable: partial.cancelable !== undefined
        ? partial.cancelable
        : (this._cancelFn !== null || (current.cancelable ?? false))
    }));
  }

  /**
   * Mark the operation as done. Auto-resets to idle after ~1200 ms.
   */
  done(): void {
    this.clearResetTimer();
    this._cancelFn = null;
    this._retryFn = null;
    this._status.update(current => ({
      ...current,
      phase: 'done',
      progress: 1,
      cancelable: false,
      retryable: false
    }));
    this._resetTimer = setTimeout(() => this.reset(), 1200);
  }

  /**
   * Mark the operation as failed with an error message.
   */
  fail(error: string): void {
    this.clearResetTimer();
    this._cancelFn = null;
    this._status.update(current => ({
      ...current,
      phase: 'error',
      cancelable: false,
      retryable: this._retryFn !== null,
      error
    }));
  }

  /**
   * Immediately reset to idle, clearing any pending auto-reset.
   */
  reset(): void {
    this.clearResetTimer();
    this._cancelFn = null;
    this._retryFn = null;
    this._status.set({ ...IDLE_STATUS });
  }

  /**
   * Register a cancel handler. Pass null to clear it.
   * Updates the `cancelable` field in the current status automatically.
   */
  registerCancel(fn: (() => void) | null): void {
    this._cancelFn = fn;
    this._status.update(current => ({ ...current, cancelable: fn !== null }));
  }

  /**
   * Invoke the registered cancel handler if one is set.
   */
  requestCancel(): void {
    if (this._cancelFn) {
      try {
        this._cancelFn();
      } finally {
        this._cancelFn = null;
        this._status.update(current => ({ ...current, cancelable: false }));
      }
    }
  }

  registerRetry(fn: (() => void) | null): void {
    this._retryFn = fn;
    this._status.update(current => ({ ...current, retryable: fn !== null }));
  }

  requestRetry(): void {
    const retry = this._retryFn;
    if (!retry) return;
    this._retryFn = null;
    this._status.update(current => ({ ...current, retryable: false }));
    retry();
  }

  private clearResetTimer(): void {
    if (this._resetTimer !== null) {
      clearTimeout(this._resetTimer);
      this._resetTimer = null;
    }
  }
}
